import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Header } from "@/components/layout/Header";
import { Zap, Sparkles } from "lucide-react";
import { useDemo } from "@/contexts/DemoContext";
import { disableDemoMode } from "@/lib/demo-data";
import { recordSignupAttempt } from "@/lib/anti-fraud";
import { trackSignup } from "@/lib/analytics";
import { generateDeviceFingerprint, fetchUserIP } from "@/lib/device-fingerprint";
import HCaptcha from "@hcaptcha/react-hcaptcha";

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [dobMonth, setDobMonth] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [dobConsentAccepted, setDobConsentAccepted] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { enableDemo } = useDemo();

  // Check if user came from demo
  const fromDemo = searchParams.get('source') === 'demo' || sessionStorage.getItem('completedLandingDemo') === 'true';

  const handleDemoMode = (role: 'user' | 'advertiser') => {
    enableDemo(role);
    toast({ 
      title: "Demo Mode Activated", 
      description: `Exploring PingEarn as ${role === 'user' ? 'a User' : 'an Advertiser'}!` 
    });
    navigate(role === 'user' ? '/dashboard' : '/advertiser');
  };

  useEffect(() => {
    // Redirect if already authenticated
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        navigate("/dashboard");
      }
    };
    checkAuth();
  }, [navigate]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate date of birth for signups
    if (!isLogin) {
      if (!dobMonth || !dobDay || !dobYear) {
        toast({
          title: "Date of Birth Required",
          description: "Please enter your complete date of birth",
          variant: "destructive"
        });
        return;
      }

      // Calculate age
      const birthDate = new Date(`${dobYear}-${dobMonth}-${dobDay}`);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      if (age < 18) {
        toast({
          title: "Age Requirement Not Met",
          description: "You must be at least 18 years old to use PingEarn",
          variant: "destructive"
        });
        return;
      }

      if (isNaN(birthDate.getTime())) {
        toast({
          title: "Invalid Date",
          description: "Please enter a valid date of birth",
          variant: "destructive"
        });
        return;
      }
    }

    if (!isLogin && !termsAccepted) {
      toast({
        title: "Terms Acceptance Required",
        description: "Please accept the Terms of Service and Privacy Policy",
        variant: "destructive"
      });
      return;
    }

    if (!isLogin && !dobConsentAccepted) {
      toast({
        title: "DOB Consent Required",
        description: "Please consent to storing your date of birth",
        variant: "destructive"
      });
      return;
    }

    // Rate limit check for signups (relaxed for better UX)
    if (!isLogin) {
      const userIP = await fetchUserIP();
      const { data: rateCheck, error: rateError } = await supabase.functions.invoke('check-signup-rate-limit', {
        body: { ip: userIP }
      });

      if (rateError || !rateCheck?.allowed) {
        toast({
          title: "Too Many Signups",
          description: "Please try again in a few minutes.",
          variant: "destructive"
        });
        return;
      }
    }

    setLoading(true);

    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        
        // Clear demo mode since user logged in with real account
        disableDemoMode();
        localStorage.removeItem('pingEarn_demo_role');
        
        // Update last login IP
        const userIP = await fetchUserIP();
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('profiles')
            .update({ last_login_ip: userIP })
            .eq('id', user.id);
        }
        
        toast({ title: "Welcome back!", description: "Logging you in..." });
        navigate("/dashboard");
      } else {
        // Check for referral code in URL
        const urlParams = new URLSearchParams(window.location.search);
        const referralCode = urlParams.get('ref');
        
        let referrerId = null;
        if (referralCode) {
          // Find the referrer
          const { data: referrer } = await supabase
            .from('profiles')
            .select('id')
            .eq('referral_code', referralCode)
            .single();
          
          if (referrer) {
            referrerId = referrer.id;
          }
        }

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            data: { 
              referred_by: referrerId
            }
          }
        });
        if (error) throw error;

        // Store device fingerprint, IP, DOB, and consent timestamps
        if (data.user) {
          const deviceFingerprint = generateDeviceFingerprint();
          const userIP = await fetchUserIP();
          const now = new Date().toISOString();
          const birthDate = `${dobYear}-${dobMonth}-${dobDay}`;
          
          await supabase
            .from('profiles')
            .update({ 
              device_fingerprint: deviceFingerprint,
              signup_ip: userIP,
              last_login_ip: userIP,
              date_of_birth: birthDate,
              age_verified_at: now,
              terms_accepted_at: now
            })
            .eq('id', data.user.id);

          // Record all legal acceptances
          await supabase
            .from('legal_acceptances')
            .insert([
              {
                user_id: data.user.id,
                document_type: 'terms',
                version: '2.0',
                ip_address: userIP,
                user_agent: navigator.userAgent
              },
              {
                user_id: data.user.id,
                document_type: 'privacy',
                version: '2.0',
                ip_address: userIP,
                user_agent: navigator.userAgent
              },
              {
                user_id: data.user.id,
                document_type: 'cookie',
                version: '2.0',
                ip_address: userIP,
                user_agent: navigator.userAgent
              },
              {
                user_id: data.user.id,
                document_type: 'dob_consent',
                version: '1.0',
                ip_address: userIP,
                user_agent: navigator.userAgent
              }
            ]);
        }

        // Clear demo mode since user created real account
        disableDemoMode();
        localStorage.removeItem('pingEarn_demo_role');
        
        // Record signup
        recordSignupAttempt();
        trackSignup('email');

        toast({ title: "Account created!", description: "Welcome to PingEarn!" });
        navigate("/onboarding");
      }
    } catch (error: any) {
      // Sanitize error messages - don't expose system internals
      const userFriendlyErrors: Record<string, string> = {
        'duplicate key': 'This email is already registered',
        'permission denied': 'Access denied',
        'relation': 'Service temporarily unavailable',
        'violates': 'Invalid input provided',
        'invalid': 'Please check your information and try again'
      };

      const errorMessage = Object.entries(userFriendlyErrors).find(([key]) =>
        error.message?.toLowerCase().includes(key)
      )?.[1] || 'An error occurred. Please try again.';

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col gradient-card">
      <Header logoTo="/" />
      
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-card border-2 animate-fade-in">
          <CardHeader>
            <CardTitle>{isLogin ? "Welcome Back" : "Get Started"}</CardTitle>
            <CardDescription>
              {fromDemo && !isLogin ? (
                <span className="text-primary font-semibold">
                  🎉 You just earned $0.50 (demo) — Let's make it real!
                </span>
              ) : isLogin ? (
                "Sign in to your account to continue earning"
              ) : (
                "Create your account and start earning today"
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAuth} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>

              {!isLogin && (
                <>
                  <div className="space-y-4 pt-2 border-t">
                    <div className="space-y-2">
                      <Label htmlFor="dob">Date of Birth</Label>
                      <p className="text-xs text-muted-foreground">You must be 18 years or older to use PingEarn</p>
                      <div className="grid grid-cols-3 gap-2">
                        <Input
                          id="dob-month"
                          type="number"
                          placeholder="MM"
                          min="1"
                          max="12"
                          value={dobMonth}
                          onChange={(e) => setDobMonth(e.target.value.padStart(2, '0'))}
                          required
                        />
                        <Input
                          id="dob-day"
                          type="number"
                          placeholder="DD"
                          min="1"
                          max="31"
                          value={dobDay}
                          onChange={(e) => setDobDay(e.target.value.padStart(2, '0'))}
                          required
                        />
                        <Input
                          id="dob-year"
                          type="number"
                          placeholder="YYYY"
                          min="1900"
                          max={new Date().getFullYear() - 18}
                          value={dobYear}
                          onChange={(e) => setDobYear(e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="flex items-start space-x-2">
                      <Checkbox 
                        id="dob-consent" 
                        checked={dobConsentAccepted}
                        onCheckedChange={(checked) => setDobConsentAccepted(checked as boolean)}
                      />
                      <label 
                        htmlFor="dob-consent" 
                        className="text-sm leading-tight cursor-pointer"
                      >
                        I consent to PingEarn storing my date of birth for age verification and compliance purposes.
                      </label>
                    </div>

                    <div className="flex items-start space-x-2">
                      <Checkbox 
                        id="terms-accept" 
                        checked={termsAccepted}
                        onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                      />
                      <label 
                        htmlFor="terms-accept" 
                        className="text-sm leading-tight cursor-pointer"
                      >
                        I agree to the{' '}
                        <a href="/legal/terms" target="_blank" className="text-primary hover:underline">
                          Terms of Service
                        </a>{' '}
                        and{' '}
                        <a href="/legal/privacy" target="_blank" className="text-primary hover:underline">
                          Privacy Policy
                        </a>
                      </label>
                    </div>
                  </div>
                </>
              )}

              <Button
                type="submit"
                className="w-full gradient-primary shadow-glow"
                disabled={loading}
              >
                {loading ? (
                  <Zap className="h-4 w-4 mr-2 animate-pulse" />
                ) : null}
                {isLogin ? "Sign In" : "Create Account"}
              </Button>
            </form>

            <div className="mt-6 space-y-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="glass"
                  className="w-full"
                  onClick={() => handleDemoMode('user')}
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Explore as User
                </Button>
                <Button
                  type="button"
                  variant="glass"
                  className="w-full"
                  onClick={() => handleDemoMode('advertiser')}
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Explore as Advertiser
                </Button>
              </div>

              <div className="text-center text-sm">
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-primary hover:underline font-medium"
                >
                  {isLogin 
                    ? "Don't have an account? Sign up" 
                    : "Already have an account? Sign in"}
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
